/**
 * Three Cups Tea - Custom Product Card Extensions
 *
 * Injects USP and cups info into product cards without modifying theme files.
 *
 * Metafields used:
 * - custom.unique_attributes (list of strings for USP)
 * - custom.cups (number of cups)
 *
 * This approach follows Maestrooo's best practices for theme extensions.
 */

(function () {
  "use strict";

  // Cache for product metafield data
  const metafieldCache = new Map();

  /**
   * Fetches product metafields via the section rendering API
   * @param {string} handle - Product handle
   * @returns {Promise<Object|null>} Product metafields or null
   */
  async function fetchProductMetafields(handle) {
    if (metafieldCache.has(handle)) {
      return metafieldCache.get(handle);
    }

    try {
      const response = await fetch(
        `/products/${handle}?section_id=custom-product-metafields`,
        { credentials: "same-origin" },
      );
      if (!response.ok) return null;

      const html = await response.text();
      const match = html.match(/data-product-metafields="([^"]+)"/);
      if (!match) return null;

      // Decode HTML entities and parse JSON
      const jsonStr = match[1]
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">");

      const data = JSON.parse(jsonStr);
      metafieldCache.set(handle, data);
      return data;
    } catch (error) {
      console.warn(
        `[Three Cups] Could not fetch metafields for: ${handle}`,
        error,
      );
      return null;
    }
  }

  /**
   * Escapes HTML to prevent XSS
   * @param {string} text
   * @returns {string}
   */
  function escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Creates USP HTML string
   * @param {Array<string>} attributes
   * @returns {string}
   */
  function createUSPHtml(attributes) {
    if (!attributes || !Array.isArray(attributes) || attributes.length === 0) {
      return "";
    }

    const items = attributes
      .map(
        (attr) =>
          `<span class="tc-product-usp__item">${escapeHtml(String(attr).toUpperCase())}</span>`,
      )
      .join('<span class="tc-product-usp__separator">·</span>');

    return `<div class="tc-product-usp">${items}</div>`;
  }

  /**
   * Creates cups count HTML string
   * @param {number|string} cups
   * @returns {string}
   */
  function createCupsHtml(cups) {
    if (!cups) return "";

    return `<span class="tc-product-cups"><span class="tc-product-cups__count">${escapeHtml(String(cups))} cups</span><span class="tc-product-cups__separator">|</span></span>`;
  }

  /**
   * Creates flavor profile radar chart SVG
   * @param {Object} flavorProfile - Object with green_vegetal, floral, fruits, spices, wood_earth, roast_nuts, cream, mineral (0-100)
   * @returns {string}
   */
  function createFlavorRadarSVG(flavorProfile) {
    if (!flavorProfile) return "";

    const {
      green_vegetal = 0,
      floral = 0,
      fruits = 0,
      spices = 0,
      wood_earth = 0,
      roast_nuts = 0,
      cream = 0,
      mineral = 0,
    } = flavorProfile;

    // Radar chart parameters
    const size = 200; // SVG viewBox size (increased to prevent label clipping)
    const center = 100;
    const maxRadius = 50; // Maximum radius for 100% value (increased for larger chart)
    const labelDistance = 60; // Distance for labels from center (closer to points)
    const levels = 5; // Number of concentric circles

    // 8 points for octagon (starting from top, clockwise)
    const points = [
      { label: "Green/Vegetal", value: green_vegetal, angle: -90 }, // Top
      { label: "Floral", value: floral, angle: -45 }, // Top-right
      { label: "Fruits", value: fruits, angle: 0 }, // Right
      { label: "Spices", value: spices, angle: 45 }, // Bottom-right
      { label: "Wood/Earth", value: wood_earth, angle: 90 }, // Bottom
      { label: "Roast/Nuts", value: roast_nuts, angle: 135 }, // Bottom-left
      { label: "Cream", value: cream, angle: 180 }, // Left
      { label: "Mineral", value: mineral, angle: 225 }, // Top-left
    ];

    // Calculate polygon points for data
    const dataPoints = points
      .map((p) => {
        const radius = (p.value / 100) * maxRadius;
        const angleRad = (p.angle * Math.PI) / 180;
        const x = center + radius * Math.cos(angleRad);
        const y = center + radius * Math.sin(angleRad);
        return `${x.toFixed(2)},${y.toFixed(2)}`;
      })
      .join(" ");

    // Calculate polygon points for outer pentagon
    const outerPoints = points
      .map((p) => {
        const angleRad = (p.angle * Math.PI) / 180;
        const x = center + maxRadius * Math.cos(angleRad);
        const y = center + maxRadius * Math.sin(angleRad);
        return `${x.toFixed(2)},${y.toFixed(2)}`;
      })
      .join(" ");

    // Generate concentric pentagon levels
    let levelPaths = "";
    for (let i = 1; i <= levels; i++) {
      const levelRadius = (maxRadius / levels) * i;
      const levelPoints = points
        .map((p) => {
          const angleRad = (p.angle * Math.PI) / 180;
          const x = center + levelRadius * Math.cos(angleRad);
          const y = center + levelRadius * Math.sin(angleRad);
          return `${x.toFixed(2)},${y.toFixed(2)}`;
        })
        .join(" ");
      levelPaths += `<polygon points="${levelPoints}" fill="none" stroke="rgba(255,255,255,0.15)" stroke-width="1" />`;
    }

    // Generate radial lines from center to each point
    const radialLines = points
      .map((p) => {
        const angleRad = (p.angle * Math.PI) / 180;
        const x = center + maxRadius * Math.cos(angleRad);
        const y = center + maxRadius * Math.sin(angleRad);
        return `<line x1="${center}" y1="${center}" x2="${x.toFixed(2)}" y2="${y.toFixed(2)}" stroke="rgba(255,255,255,0.15)" stroke-width="1" />`;
      })
      .join("");

    // Generate labels positioned outside the pentagon
    const labels = points
      .map((p) => {
        const angleRad = (p.angle * Math.PI) / 180;
        const x = center + labelDistance * Math.cos(angleRad);
        const y = center + labelDistance * Math.sin(angleRad);

        // Determine text anchor based on position
        let textAnchor = "middle";
        if (x < center - 5) textAnchor = "end";
        if (x > center + 5) textAnchor = "start";

        // Adjust vertical alignment
        let dy = "0.35em"; // middle
        if (y < center - 5) dy = "0.9em"; // top
        if (y > center + 5) dy = "-0.1em"; // bottom

        return `<text
        x="${x.toFixed(2)}"
        y="${y.toFixed(2)}"
        text-anchor="${textAnchor}"
        dy="${dy}"
        class="tc-flavor-radar__point-label"
        fill="white"
        font-size="6"
        font-weight="500"
        letter-spacing="0.05em"
      >${p.label.toUpperCase()}</text>`;
      })
      .join("");

    return `
      <svg class="tc-flavor-radar__svg" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">
        <!-- Background levels and grid -->
        ${radialLines}
        ${levelPaths}

        <!-- Outer pentagon border -->
        <polygon points="${outerPoints}" fill="none" stroke="rgba(255,255,255,0.3)" stroke-width="2" />

        <!-- Data polygon -->
        <polygon
          class="tc-flavor-radar__data"
          points="${dataPoints}"
          fill="rgba(255,255,255,0.2)"
          stroke="rgba(255,255,255,0.6)"
          stroke-width="1.5"
          stroke-linejoin="round"
        />

        <!-- Data points as circles -->
        ${points
          .map((p) => {
            const radius = (p.value / 100) * maxRadius;
            const angleRad = (p.angle * Math.PI) / 180;
            const x = center + radius * Math.cos(angleRad);
            const y = center + radius * Math.sin(angleRad);
            return `<circle cx="${x.toFixed(2)}" cy="${y.toFixed(2)}" r="1.5" fill="white" />`;
          })
          .join("")}

        <!-- Point labels -->
        ${labels}
      </svg>
    `;
  }

  /**
   * Creates flavor radar HTML wrapper
   * @param {Object} flavorProfile
   * @returns {string}
   */
  function createFlavorRadarHtml(flavorProfile) {
    if (!flavorProfile) return "";

    const svg = createFlavorRadarSVG(flavorProfile);
    if (!svg) return "";

    return `<div class="tc-flavor-radar">${svg}</div>`;
  }

  /**
   * Enhances a single product card
   * @param {HTMLElement} card - The product-card element
   */
  async function enhanceProductCard(card) {
    // Skip if already enhanced or processing
    if (
      card.dataset.tcEnhanced === "true" ||
      card.dataset.tcProcessing === "true"
    )
      return;
    card.dataset.tcProcessing = "true";

    const handle = card.getAttribute("handle");
    if (!handle) {
      card.dataset.tcProcessing = "false";
      return;
    }

    try {
      const metafields = await fetchProductMetafields(handle);
      if (!metafields) {
        card.dataset.tcEnhanced = "true";
        return;
      }

      const { unique_attributes, cups, flavor_profile } = metafields;

      // First, find the price-list and prepend cups to it
      const priceList = card.querySelector("price-list, .price-list");
      let insertionTarget = priceList; // Track where to insert USP

      if (priceList && cups) {
        const cupsHtml = createCupsHtml(cups);
        if (cupsHtml) {
          // Wrap cups and price in a flex container
          const parent = priceList.parentElement;
          const wrapper = document.createElement("div");
          wrapper.className = "tc-price-cups-wrapper";

          parent.insertBefore(wrapper, priceList);
          wrapper.insertAdjacentHTML("afterbegin", cupsHtml);
          wrapper.appendChild(priceList);
          insertionTarget = wrapper; // Update insertion target to the wrapper
        }
      }

      // Insert USP as overlay on product image
      const figureEl = card.querySelector(".product-card__figure");
      if (figureEl && unique_attributes && unique_attributes.length > 0) {
        const uspHtml = createUSPHtml(unique_attributes);
        if (uspHtml) {
          // Insert as absolute positioned overlay inside figure
          figureEl.insertAdjacentHTML("beforeend", uspHtml);
        }
      }

      // Insert flavor radar as overlay on product image
      if (figureEl && flavor_profile) {
        const radarHtml = createFlavorRadarHtml(flavor_profile);
        if (radarHtml) {
          // Insert as absolute positioned overlay inside figure
          figureEl.insertAdjacentHTML("beforeend", radarHtml);
        }
      }

      card.dataset.tcEnhanced = "true";
    } catch (error) {
      console.warn(`[Three Cups] Error enhancing card for: ${handle}`, error);
    } finally {
      card.dataset.tcProcessing = "false";
    }
  }

  /**
   * Processes all product cards on the page
   */
  function processAllCards() {
    const cards = document.querySelectorAll(
      'product-card:not([data-tc-enhanced="true"]):not([data-tc-processing="true"])',
    );
    cards.forEach((card) => enhanceProductCard(card));
  }

  /**
   * Sets up MutationObserver for dynamically loaded cards
   */
  function observeNewCards() {
    const observer = new MutationObserver((mutations) => {
      let hasNewCards = false;

      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;

          // Check if the added node is a product card
          if (
            node.matches &&
            node.matches('product-card:not([data-tc-enhanced="true"])')
          ) {
            hasNewCards = true;
          }

          // Check for product cards within the added node
          if (node.querySelectorAll) {
            const cards = node.querySelectorAll(
              'product-card:not([data-tc-enhanced="true"])',
            );
            if (cards.length > 0) hasNewCards = true;
          }
        }
      }

      // Debounce processing
      if (hasNewCards) {
        requestAnimationFrame(() => {
          processAllCards();
          // Attach click handlers to any new radars
          setTimeout(() => {
            attachRadarClickHandlers();
          }, 100);
        });
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Handle flavor radar click to toggle expansion
   */
  function handleRadarClick(event) {
    const radar = event.currentTarget;
    const isExpanded = radar.classList.contains("is-expanded");

    if (isExpanded) {
      // Collapse
      radar.classList.remove("is-expanded");
    } else {
      // Expand this radar
      radar.classList.add("is-expanded");

      // Close any other expanded radars on the page
      const otherRadars = document.querySelectorAll(
        ".tc-flavor-radar.is-expanded",
      );
      otherRadars.forEach((otherRadar) => {
        if (otherRadar !== radar) {
          otherRadar.classList.remove("is-expanded");
        }
      });
    }
  }

  /**
   * Attach click handlers to all radars
   */
  function attachRadarClickHandlers() {
    const radars = document.querySelectorAll(".tc-flavor-radar");
    radars.forEach((radar) => {
      // Remove existing listener if any
      radar.removeEventListener("click", handleRadarClick);
      // Add new listener
      radar.addEventListener("click", handleRadarClick);
    });
  }

  /**
   * Initialize
   */
  function init() {
    // Process existing cards
    processAllCards();

    // Watch for new cards (infinite scroll, quick view, etc.)
    observeNewCards();

    // Attach click handlers after a short delay to ensure cards are enhanced
    setTimeout(() => {
      attachRadarClickHandlers();
    }, 500);

    console.log("[Three Cups] Product card extensions initialized");
  }

  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
